import { createContext } from "react";

/**
 * Represents a single breadcrumb item in the navigation
 */
export interface BreadcrumbItem {
    /**
     * Display label for the breadcrumb
     */
    label: string;
    /**
     * Optional path/route for the breadcrumb link
     * If not provided, the breadcrumb will not be clickable
     */
    path?: string;
    /**
     * Whether this is the currently active/selected breadcrumb
     * Active breadcrumbs are typically not clickable and styled differently
     */
    isActive?: boolean;
    /**
     * Whether to open the link in a new tab/window
     * Only applicable if `path` is provided
     */
    openInNewTab?: boolean;
    /**
     * Optional icon to display alongside the label
     */
    icon?: React.ReactNode;
}

/**
 * Context interface for managing breadcrumb navigation state
 */
export interface BreadcrumbContextType {
    /**
     * Current array of breadcrumb items
     */
    breadcrumbs: BreadcrumbItem[];
    /**
     * Replace the entire breadcrumb array
     */
    setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
    /**
     * Add a single breadcrumb to the end of the current array
     */
    addBreadcrumb: (item: BreadcrumbItem) => void;
    /**
     * Remove all breadcrumbs
     */
    clearBreadcrumbs: () => void;
    /**
     * Update a specific breadcrumb by index
     */
    updateBreadcrumb: (index: number, item: Partial<BreadcrumbItem>) => void;
    /**
     * Remove a specific breadcrumb by index
     */
    removeBreadcrumb: (index: number) => void;
}

/**
 * React Context for breadcrumb navigation state management
 */
export const BreadcrumbContext = createContext<
    BreadcrumbContextType | undefined
>(undefined);
